/**
 * MEDIS v2 — routing + client.
 * ════════════════════════════════════════════════════════════════════════════
 * The v2 MEDIS API differs from v1 in three ways:
 *   1. Auth      — Azure EasyAuth: ONLY the `X-ZUMO-AUTH` session token (NO
 *                  `Authorization` Bearer, NO access-code/validator handshake,
 *                  NO `/NewSession`). Tokens are per host and lazy: the one for
 *                  the production host is co-issued with the OAuth
 *                  tokens (the `azure` scope); any other host's is fetched on
 *                  demand from the auth server (`lib/medis/zumoToken.ts`) the
 *                  first time a call goes there, and renewed the same way on
 *                  a 401 — all handled by the v2 client.
 *   2. Envelope  — the body IS the inner `Response` (no `{ State, Response }`).
 *   3. Casing    — fields are PascalCase (see `./types` + `./parse`).
 *
 * Two independent decisions, two mechanisms:
 *   - WHICH calls use v2 is decided in code, by `MEDIS_V2_ROUTES` (set an entry
 *     to `null` to send that call back through the v1 flow).
 *   - WHERE a v2 call goes (host + path) is decided by the server-driven dynamic
 *     endpoint registry — the `api_version === "v2"` rows of
 *     `GET /v2/endpoint-urls`, resolved by `v2Url()` via
 *     `endpointUrls.resolveV2()`. `env.medisV2BaseUrl` is only the static
 *     fallback when no v2 row matches.
 * ════════════════════════════════════════════════════════════════════════════
 */
import { createZumoClient } from '@/lib/api/client';
import { endpointUrls } from '../endpointUrls';

/**
 * EasyAuth client for v2: injects the host's `X-ZUMO-AUTH` (issued on demand
 * when the session has none for that host; no Bearer, no session) and renews
 * it on a 401. No fixed baseURL — each call passes an absolute URL resolved
 * per call by `v2Url()`.
 */
export const medisV2Api = createZumoClient();

/** A single v2 call's routing: how to find its current host + path. */
export interface V2Route {
  /**
   * `api_name` of this call in the dynamic registry (`api_version: "v2"`).
   * Same lower-case vocabulary as the v1 rows (`getpatient`, `download`, …)
   * so an admin configures both versions of a call under one name.
   */
  apiName: string;
  /** Endpoint path appended to the resolved base (e.g. `/v2/GetPatient`). */
  path: string;
}

/**
 * Per-call v2 switch. Set an entry to `null` to send that call back through
 * the v1 dynamic model instead. Emptying this object (all `null`) disables v2
 * entirely. Calls with no entry here (`/Timetable`, `/SetAppointment`) have no
 * v2 equivalent yet and stay on v1.
 */
export const MEDIS_V2_ROUTES: {
  getPatient: V2Route | null;
  getPatientRefresh: V2Route | null;
  getAccessList: V2Route | null;
  getAppointments: V2Route | null;
  getDocument: V2Route | null;
  registerPatient: V2Route | null;
  detachPatient: V2Route | null;
} = {
  getPatient: { apiName: 'getpatient', path: '/v2/GetPatient' },
  // v2 only — lightweight refresh of the primary patient's attach code +
  // `AttachedBy`, called on every entry to the patients page (no v1 twin).
  getPatientRefresh: { apiName: 'getpatientrefresh', path: '/v2/GetPatientRefresh' },
  getAccessList: { apiName: 'getaccesslist', path: '/v2/GetAccessList' },
  getAppointments: { apiName: 'getappointments', path: '/v2/GetAppointments' },
  // Same `api_name` as the v1 `/GetDocument` row.
  getDocument: { apiName: 'download', path: '/v2/GetDocument' },
  registerPatient: { apiName: 'registerpatient', path: '/v2/RegisterPatient' },
  // v2 only — there is no v1 DetachPatient.
  detachPatient: { apiName: 'detachpatient', path: '/v2/DetachPatient' },
};

/**
 * Resolves the absolute URL for a v2 route through the dynamic registry
 * (loading it on first use), falling back to `env.medisV2BaseUrl + route.path`.
 * Never throws: a failed registry load just yields the static URL.
 */
export async function v2Url(route: V2Route): Promise<string> {
  await endpointUrls.ensureLoaded();
  return endpointUrls.resolveV2(route.apiName, route.path);
}
